import { Offer } from '@appTypes/essential-ui/Offer';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BuildPathFromParameters } from '@services/api/ApiMethods';
import { GetOffersFromPath } from '@services/api/GenericClient';

export type OffersState = {
  offers: readonly Offer[];
  loading: boolean;
  error: boolean;
  currentPage: number;
  keepUsersOnSamePage: boolean;
  currentPageSize: string;
};

const initialOffersState: OffersState = {
  offers: [],
  loading: false,
  error: false,
  currentPage: 1,
  keepUsersOnSamePage: false,
  currentPageSize: 'Show 10',
};

export const getOffers = createAsyncThunk(
  'offers/getOffers',
  async (params: { showPublicOffers: boolean; isAdminUser: boolean }): Promise<Offer[]> => {
    try {
      const method = '/offers/offers';
      //TODO: page size will come from subscription
      const pageSize = '1500';
      //TODO: includePublicOffers will depend on subscription
      const includePublicOffers = params.showPublicOffers.toString();

      const data = {
        includePublicOffers: includePublicOffers,
        pagesize: pageSize,
        isAdminUser: params.isAdminUser.toString(),
      };
      const path = BuildPathFromParameters(method, data);
      const response = await GetOffersFromPath<Offer>(path);

      if (
        response &&
        response.errorMessage &&
        response.errorMessage.toLocaleLowerCase() === 'no offers found'
      ) {
        throw new Error('No offers found');
      }

      if (response && response.resources) {
        return response.resources;
      }

      return [];
    } catch (err) {
      throw new Error('Failed to load offers');
    }
  },
);

const offersSlice = createSlice({
  name: 'offers',
  initialState: initialOffersState,
  reducers: {
    offersLoading(state) {
      state.loading = true;
      state.error = false;
    },
    offersLoaded(state, action: PayloadAction<Offer[]>) {
      state.offers = action.payload;
      state.loading = false;
      state.error = false;
    },
    offersError(state) {
      state.offers = [];
      state.loading = false;
      state.error = true;
    },
    setOffersIncrementNoteCount(
      state,
      action: PayloadAction<{ closedDealId: string; addedNotesCount: number }>,
    ) {
      const { closedDealId, addedNotesCount } = action.payload;
      const offer = state.offers.find((e) => e.id === closedDealId);

      if (offer) {
        offer.dealNotesCount += addedNotesCount;
      }
    },
    setOffersUpdateLastUpdate(
      state,
      action: PayloadAction<{ closedDealId: string; daysSinceLastUpdate: number }>,
    ) {
      const { closedDealId, daysSinceLastUpdate } = action.payload;
      const offer = state.offers.find((e) => e.id === closedDealId);

      if (offer) {
        offer.daysSinceLastUpdate = daysSinceLastUpdate;
      }
    },
    setOffersInactive(state, action: PayloadAction<string>) {
      state.offers = state.offers.filter((offer) => offer.id !== action.payload);
    },
    setOffersCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload;
    },
    setOffersKeepUsersOnSamePage(state, action: PayloadAction<boolean>) {
      state.keepUsersOnSamePage = action.payload;
    },
    setOffersCurrentPageSize(state, action: PayloadAction<string>) {
      state.currentPageSize = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOffers.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(getOffers.fulfilled, (state, action) => {
      if (action.payload !== undefined) {
        state.offers = action.payload;
      }
      state.loading = false;
      state.error = false;
    });
    builder.addCase(getOffers.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
  },
});

export const {
  offersLoading,
  offersLoaded,
  offersError,
  setOffersIncrementNoteCount,
  setOffersUpdateLastUpdate,
  setOffersInactive,
  setOffersCurrentPage,
  setOffersKeepUsersOnSamePage,
  setOffersCurrentPageSize,
} = offersSlice.actions;

export default offersSlice.reducer;
